import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { t } from '../../i18n/nl.js';
import { toAmsterdamTime, toAmsterdamDate } from '../../utils/dateUtils.js';
import { lesCoachNaam } from '../../utils/lesCoachUtils.js';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';

function fmtLesDateTime(isoString) {
  if (!isoString) return '—';
  try {
    const d = toAmsterdamDate(isoString);
    const tijd = toAmsterdamTime(isoString);
    return format(d, 'EEEE d MMMM yyyy', { locale: nl }) + ', ' + tijd;
  } catch { return isoString; }
}

export default function ClientLessenPage() {
  const { user } = useAuth();
  const { showError } = useToast();
  const [lessen, setLessen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('aankomend');
  const [annulerenId, setAnnulerenId] = useState(null);
  const [bevestigId, setBevestigId] = useState(null);

  async function load() {
    setLoading(true);
    setError('');
    try {
      const { data, error: err } = await supabase
        .from('lesson_enrollments')
        .select(`
          id, status, enrolled_at,
          group_lessons(
            id, name, starts_at, duration_minutes,
            cancel_deadline_hours,
            locations(name),
            coach:coach_basic_profiles!group_lessons_coach_id_fkey(full_name),
            executing_coach:coach_basic_profiles!group_lessons_executing_coach_id_fkey(full_name)
          )
        `)
        .eq('client_id', user.id)
        .neq('status', 'cancelled');
      if (err) throw err;
      setLessen(data || []);
    } catch (err) {
      setError(t('mijnLessen.foutLaden') + err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [user?.id]);

  async function handleAnnuleer(enrollment) {
    setAnnulerenId(enrollment.id);
    try {
      const { data: result, error: err } = await supabase
        .rpc('unenroll_client', { p_lesson_id: enrollment.group_lessons.id });
      if (err) throw err;
      if (result?.error) {
        const errMap = {
          niet_ingeschreven: t('mijnLessen.errNietIngeschreven'),
          deadline_verstreken: t('mijnLessen.errDeadlineVerstreken'),
        };
        showError(errMap[result.error] || t('mijnLessen.errOnbekend'));
        return;
      }
      setBevestigId(null);
      await load();
    } catch (err) {
      showError(t('mijnLessen.annuleerFout') + err.message);
    } finally {
      setAnnulerenId(null);
    }
  }

  function canUnenroll(enrollment) {
    const lesson = enrollment.group_lessons;
    if (!lesson?.starts_at) return false;
    if (enrollment.status === 'waitlisted') return false;
    const deadline = new Date(lesson.starts_at).getTime()
      - (lesson.cancel_deadline_hours || 0) * 3600000;
    return Date.now() < deadline;
  }

  const now = new Date().toISOString();
  const aankomend = lessen
    .filter(e => e.group_lessons?.starts_at >= now)
    .sort((a, b) => a.group_lessons.starts_at.localeCompare(b.group_lessons.starts_at));
  const gevolgd = lessen
    .filter(e => e.group_lessons?.starts_at < now)
    .sort((a, b) => b.group_lessons.starts_at.localeCompare(a.group_lessons.starts_at));

  const weergave = tab === 'aankomend' ? aankomend : gevolgd;

  return (
    <div>
      {/* Bevestigingsdialoog annuleren */}
      {bevestigId && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{t('mijnLessen.annuleerTitel')}</h2>
              <button className="modal-close" onClick={() => setBevestigId(null)}>✕</button>
            </div>
            <p style={{ marginBottom: 20, fontSize: 14, color: 'var(--color-text-muted)' }}>
              {t('mijnLessen.annuleerBevestig')}
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost" onClick={() => setBevestigId(null)} disabled={!!annulerenId}>
                {t('mijnLessen.annuleerNee')}
              </button>
              <button
                className="btn btn-danger"
                onClick={() => handleAnnuleer(lessen.find(e => e.id === bevestigId))}
                disabled={!!annulerenId}
              >
                {annulerenId ? t('mijnLessen.annulerenBezig') : t('mijnLessen.annuleerJa')}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="page-header">
        <div>
          <h1 className="page-title">{t('mijnLessen.title')}</h1>
        </div>
      </div>

      {error && <div className="error-msg" style={{ marginBottom: 20 }}>{error}</div>}

      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        <button
          className={`btn ${tab === 'aankomend' ? 'btn-primary' : 'btn-ghost'} btn-sm`}
          onClick={() => setTab('aankomend')}
        >
          {t('mijnLessen.tabAankomend')} ({aankomend.length})
        </button>
        <button
          className={`btn ${tab === 'gevolgd' ? 'btn-primary' : 'btn-ghost'} btn-sm`}
          onClick={() => setTab('gevolgd')}
        >
          {t('mijnLessen.tabGevolgd')} ({gevolgd.length})
        </button>
      </div>

      {loading ? (
        <div className="loading-center"><div className="spinner" /></div>
      ) : weergave.length === 0 ? (
        <div className="card">
          <p className="text-muted">
            {tab === 'aankomend' ? t('mijnLessen.geenAankomend') : t('mijnLessen.geenGevolgd')}
          </p>
        </div>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{t('mijnLessen.colLes')}</th>
                  <th>{t('mijnLessen.colDatumTijd')}</th>
                  <th>{t('mijnLessen.colLocatie')}</th>
                  <th>{t('mijnLessen.colCoach')}</th>
                  <th>{t('mijnLessen.colStatus')}</th>
                  {tab === 'aankomend' && <th style={{ textAlign: 'right' }}></th>}
                </tr>
              </thead>
              <tbody>
                {weergave.map(e => (
                  <tr key={e.id}>
                    <td className="font-medium">{e.group_lessons?.name || '—'}</td>
                    <td className="text-muted">{fmtLesDateTime(e.group_lessons?.starts_at)}</td>
                    <td className="text-muted">{e.group_lessons?.locations?.name || '—'}</td>
                    <td className="text-muted">{lesCoachNaam(e.group_lessons) || '—'}</td>
                    <td>
                      <span style={{
                        display: 'inline-block',
                        padding: '2px 8px',
                        borderRadius: 99,
                        fontSize: 11,
                        fontWeight: 600,
                        background: tab === 'aankomend' ? '#eff6ff' : '#f0fdf4',
                        color: tab === 'aankomend' ? '#2563eb' : '#16a34a',
                      }}>
                        {tab === 'aankomend'
                          ? (e.status === 'waitlisted' ? t('mijnLessen.badgeWachtlijst') : t('mijnLessen.badgeAankomend'))
                          : t('mijnLessen.badgeGevolgd')}
                      </span>
                    </td>
                    {tab === 'aankomend' && (
                      <td style={{ textAlign: 'right' }}>
                        {canUnenroll(e) && (
                          <button
                            className="btn btn-ghost btn-sm"
                            style={{ color: 'var(--color-danger)', fontSize: 12 }}
                            onClick={() => setBevestigId(e.id)}
                            disabled={annulerenId === e.id}
                          >
                            {t('mijnLessen.annuleerKnop')}
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
